import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, CalendarCheck } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Logo } from "./Logo";
import { NAV_LINKS } from "../../data/content";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    if (href.startsWith("/")) {
      navigate(href);
      window.scrollTo({ top: 0 });
      return;
    }
    const scrollToHash = () => {
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    };
    if (window.location.pathname !== "/") {
      navigate("/");
      setTimeout(scrollToHash, 350);
    } else {
      scrollToHash();
    }
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow,padding] duration-500 ${
        scrolled ? "bg-white/80 backdrop-blur-xl border-b border-black/5 py-3 shadow-[0_8px_30px_rgba(3,31,20,0.05)]" : "bg-transparent py-5"
      }`}
      data-testid="navbar"
    >
      <nav className="mx-auto flex max-w-[1400px] items-center justify-between px-5 sm:px-8">
        <Logo variant={scrolled ? "light" : "dark"} />
        <div className="hidden items-center gap-1 xl:flex">
          {NAV_LINKS.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
              onClick={() => go(l.href)}
              className={`relative rounded-full px-3 py-2 text-sm font-medium transition-colors duration-300 hover:text-gold ${scrolled ? "text-charcoal/70" : "text-off-white/85"}`}
            >
              {l.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <button
            data-testid="nav-book-consultation-btn"
            onClick={() => go("#contact")}
            className="hidden items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-semibold text-emerald-darkest shadow-[0_6px_20px_rgba(212,175,55,0.35)] transition-transform duration-300 hover:-translate-y-0.5 sm:flex"
          >
            <CalendarCheck size={16} /> Book Consultation
          </button>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((o) => !o)}
            className={`flex h-10 w-10 items-center justify-center rounded-full border xl:hidden ${scrolled ? "border-black/10 text-charcoal" : "border-white/25 text-off-white"}`}
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden bg-white/95 backdrop-blur-xl xl:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col gap-1 px-6 py-5">
              {NAV_LINKS.map((l) => (
                <button
                  key={l.href}
                  onClick={() => go(l.href)}
                  className="rounded-lg px-3 py-3 text-left text-base font-medium text-charcoal/80 hover:bg-off-white"
                >
                  {l.label}
                </button>
              ))}
              <button
                onClick={() => go("#contact")}
                className="mt-3 rounded-full bg-gold px-5 py-3 text-sm font-semibold text-emerald-darkest"
              >
                Book Consultation
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
